import { ReactElement, useMemo } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import { useSubscription } from 'services/hooks';

import IntervalLabel from '../IntervalLabel';
import {
  StyledSubscriptionBox,
  StyledSubscriptionGrid,
  StyledAccountTypeAndLabelContainer,
  StyledProductDescription,
  StyledProductInfo,
} from './styled';

const SubscriptionInfo = (): ReactElement => {
  const { t } = useTranslation();
  const { isFreeAccount, subscription, isSubscriptionCancellationRequested } =
    useSubscription();

  const productName = useMemo(
    () => (isFreeAccount ? 'Free' : subscription?.product.name || '-'),
    [isFreeAccount, subscription]
  );

  const getProductEndDate = () => {
    if (isFreeAccount || !subscription) {
      return '-';
    }

    return new Date(subscription?.period.end.date).toLocaleDateString(
      undefined,
      {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      }
    );
  };

  const productEndDate = getProductEndDate();

  const productPrice = useMemo(() => {
    if (isFreeAccount || !subscription || isSubscriptionCancellationRequested) {
      return '-';
    }

    return `$${subscription.price.unitAmount / 100}`;
  }, [isFreeAccount, subscription, isSubscriptionCancellationRequested]);

  return (
    <StyledSubscriptionGrid>
      <StyledSubscriptionBox>
        <StyledProductDescription>
          {t('account:subscription:accountType')}
        </StyledProductDescription>
        <StyledAccountTypeAndLabelContainer>
          <StyledProductInfo>
            <Trans i18nKey="account:subscription:productName ">
              {{ productName }}
            </Trans>
          </StyledProductInfo>
          {!isFreeAccount && <IntervalLabel />}
        </StyledAccountTypeAndLabelContainer>
      </StyledSubscriptionBox>
      <StyledSubscriptionBox>
        <StyledProductDescription>
          {isSubscriptionCancellationRequested
            ? t('account:subscription:planEndOn')
            : t('account:subscription:nextInvoiceDate')}
        </StyledProductDescription>
        <StyledProductInfo>
          <Trans i18nKey="account:subscription:subscriptionEnd">
            {{ productEndDate }}
          </Trans>
        </StyledProductInfo>
      </StyledSubscriptionBox>
      <StyledSubscriptionBox>
        <StyledProductDescription>
          {t('account:subscription:nextInvoiceAmount')}
        </StyledProductDescription>
        <StyledProductInfo>
          <Trans i18nKey="account:subscription:subscriptionPrice">
            {{ productPrice }}
          </Trans>
        </StyledProductInfo>
      </StyledSubscriptionBox>
    </StyledSubscriptionGrid>
  );
};

export default SubscriptionInfo;
